import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, ArrowBack, ShoppingCart } from '@mui/icons-material';
import apiClient from '../lib/axios';

interface OrderListItem {
  id: string;
  trackingUUID: string;
  status: string;
  patientName: string;
  pharmacyChain?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
  totalAmount: string;
  itemCount: number;
  createdAt: string;
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  READY_FOR_PICKUP: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const statusTranslationKeys: Record<string, string> = {
  PENDING: 'status.pending',
  IN_PROGRESS: 'status.inProgress',
  READY_FOR_PICKUP: 'status.readyForPickup',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
};

export const OrderList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const { data } = await apiClient.get<OrderListItem[]>('/orders');
      return data;
    },
  });

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('errors.failedToLoadOrders')}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 2 : 0 }}>
              <ShoppingCart sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Typography variant="h4" component="h1" fontWeight="bold">
                {t('doctor.orders.title')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', mt: isMobile ? 2 : -5 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/doctor/dashboard')}
                fullWidth={isMobile}
              >
                {t('doctor.orders.backToDashboard')}
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/doctor/orders/new')}
                fullWidth={isMobile}
              >
                {t('doctor.orders.createNew')}
              </Button>
            </Box>
          </Box>

          {!orders || orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShoppingCart sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('doctor.orders.noOrdersYet')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('doctor.orders.createFirstOrderMessage')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/doctor/orders/new')}
              >
                {t('doctor.orders.createFirstOrder')}
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {!isMobile && <TableCell>{t('doctor.orders.orderNumber')}</TableCell>}
                    <TableCell>{t('doctor.orders.patient')}</TableCell>
                    <TableCell>{t('doctor.orders.pharmacy')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    {!isMobile && <TableCell align="right">{t('common.total')}</TableCell>}
                    <TableCell>{t('common.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/doctor/orders/${order.id}`)}
                    >
                      {!isMobile && (
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            #{order.trackingUUID.substring(0, 8).toUpperCase()}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>{order.patientName}</TableCell>
                      <TableCell>{order.pharmacyChain?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(statusTranslationKeys[order.status] ?? 'status.pending')}
                          color={statusColors[order.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="right">
                          ${parseFloat(order.totalAmount.toString()).toFixed(2)}
                        </TableCell>
                      )}
                      <TableCell>
                        {new Date(order.createdAt).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Container>
  );
};
